<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Room Operations'"
      [canGoBack]="!windowService.isDesktopRes" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">

    @if (errorMessage) {
      <tidy-error-message [text]="errorMessage" />
    }

    @if (loaded && unit) {

      <!-- Header -->
      <tidy-card>
        <tidy-row class="vertical-align-center">
          <tidy-text [header]="unit.property_name + ' · ' + unit.unit" [translate]="false" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image [src]="'assets/svg/location-outline.svg'" class="body-size" />
          <tidy-text [helper]="unit.address" [translate]="false" />
        </tidy-row>
        @if (occupancyLabel) {
          <tidy-row class="vertical-align-center">
            <tidy-image [src]="'assets/svg/people-outline.svg'" class="body-size" />
            <tidy-text [helper]="occupancyLabel" [translate]="false" />
          </tidy-row>
        }
      </tidy-card>

      <!-- Shared expenses -->
      <tidy-row><tidy-text [title]="'Shared expenses'" /></tidy-row>
      @if (sharedExpenses.length === 0) {
        <tidy-alert><tidy-row><tidy-text [body]="'No shared expenses recorded for this unit.'" /></tidy-row></tidy-alert>
      }
      @for (view of sharedExpenses; track view.expense.id) {
        <tidy-card>
          <tidy-row [alignLastItemRight]="true">
            <tidy-text [body]="view.expense.description" [translate]="false" />
            <tidy-text
              [body]="view.expense.status"
              [translate]="false"
              [ngStyle]="{ color: view.statusColor }" />
          </tidy-row>
          <tidy-row class="vertical-align-center">
            <tidy-image [src]="'assets/svg/cash-outline.svg'" class="body-size" />
            <tidy-text [helper]="'$' + view.expense.amount.toLocaleString('en-US') + ' · ' + view.expense.period" [translate]="false" />
          </tidy-row>
          <tidy-divider />
          <tidy-row class="extra-top-padding"><tidy-text [helper]="'Allocation'" /></tidy-row>
          @for (row of view.expense.allocation; track row.space_name) {
            <tidy-row [alignLastItemRight]="true">
              <tidy-text [body]="row.space_name + ' · ' + row.tenant_name" [translate]="false" />
              <tidy-text [body]="'$' + row.share.toLocaleString('en-US')" [translate]="false" />
            </tidy-row>
          }
        </tidy-card>
      }

      <!-- Applicant waitlist -->
      <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
        <tidy-text [title]="'Applicant waitlist'" />
        <tidy-text [actionBody]="'Add prospect'" (action)="addWaitlistEntry()" class="white-link" />
      </tidy-row>
      @if (waitlist.length === 0) {
        <tidy-alert><tidy-row><tidy-text [body]="'No one on the waitlist for this unit yet.'" /></tidy-row></tidy-alert>
      }
      @for (view of waitlist; track view.entry.id) {
        <tidy-card>
          <tidy-row [alignLastItemRight]="true">
            <tidy-text [body]="view.entry.name" [translate]="false" />
            <tidy-text
              [body]="view.entry.status"
              [translate]="false"
              [ngStyle]="{ color: view.statusColor }" />
          </tidy-row>
          <tidy-row class="vertical-align-center">
            <tidy-image [src]="'assets/svg/calendar-outline.svg'" class="body-size" />
            <tidy-text [helper]="'Wants to move in ' + (view.entry.desired_move_in | date:'MMM d, yyyy')" [translate]="false" />
          </tidy-row>
          @if (view.entry.room_preference) {
            <tidy-row class="vertical-align-center">
              <tidy-image [src]="'assets/svg/bed-outline.svg'" class="body-size" />
              <tidy-text [helper]="'Prefers ' + view.entry.room_preference" [translate]="false" />
            </tidy-row>
          }
        </tidy-card>
      }

      <!-- Upkeep: only right-align when the link is actually there, otherwise the
           lone title gets pushed right by align-last-right's last-child rule -->
      <tidy-row [alignLastItemRight]="canLogTurnoverClean" class="vertical-align-center">
        <tidy-text [title]="'Upkeep'" />
        @if (canLogTurnoverClean) {
          <tidy-text [actionBody]="'Log turnover clean'" (action)="logTurnoverClean()" class="white-link" />
        }
      </tidy-row>
      @if (roomUpkeepTasks.length === 0 && commonAreaUpkeepTasks.length === 0) {
        <tidy-alert><tidy-row><tidy-text [body]="'No upkeep schedule set for this unit.'" /></tidy-row></tidy-alert>
      }

      @if (roomUpkeepTasks.length > 0) {
        <tidy-row><tidy-text [helper]="'Rooms'" /></tidy-row>
        @for (task of roomUpkeepTasks; track task.id) {
          <tidy-card>
            <tidy-row [alignLastItemRight]="true">
              <tidy-text [body]="task.task" [translate]="false" />
              <tidy-text [body]="task.frequency" [translate]="false" />
            </tidy-row>
            <tidy-row class="vertical-align-center">
              <tidy-image [src]="'assets/svg/bed-outline.svg'" class="body-size" />
              <tidy-text [helper]="task.area" [translate]="false" />
            </tidy-row>
            <tidy-row [alignLastItemRight]="true">
              <tidy-text [helper]="'Next due ' + (task.next_due | date:'MMM d, yyyy')" [translate]="false" />
              <tidy-text [helper]="'Assigned to ' + task.assigned_to" [translate]="false" />
            </tidy-row>
          </tidy-card>
        }
      }

      @if (commonAreaUpkeepTasks.length > 0) {
        <tidy-row><tidy-text [helper]="'Common areas'" /></tidy-row>
        @for (task of commonAreaUpkeepTasks; track task.id) {
          <tidy-card>
            <tidy-row [alignLastItemRight]="true">
              <tidy-text [body]="task.task" [translate]="false" />
              <tidy-text [body]="task.frequency" [translate]="false" />
            </tidy-row>
            <tidy-row class="vertical-align-center">
              <tidy-image [src]="'assets/svg/home-outline.svg'" class="body-size" />
              <tidy-text [helper]="task.area" [translate]="false" />
            </tidy-row>
            <tidy-row [alignLastItemRight]="true">
              <tidy-text [helper]="'Next due ' + (task.next_due | date:'MMM d, yyyy')" [translate]="false" />
              <tidy-text [helper]="'Assigned to ' + task.assigned_to" [translate]="false" />
            </tidy-row>
          </tidy-card>
        }
      }
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
